/**
 * The branded application chrome — one shell for every signed-in surface.
 *
 * Desktop: top bar + left sidebar, which collapses to an icon rail (the choice is remembered per
 * browser). Mobile: top bar + app-like bottom tab bar. The chrome lives in a single place so the
 * header logo, sidebar treatment and active-nav styling only ever need to be changed once.
 * RoleLayout (patient / doctor / pharmacist / support) and AdminLayout both render through this.
 *
 * A surface that passes `profile` gets an account button (initials + name) top right that opens
 * it in a panel sliding in from the right, so the page underneath keeps its state.
 *
 * All colour comes from the tokens in src/index.css via the .nav-link /
 * .nav-tab / bg-brand-* utilities — no literal values here.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

import { AppFooter } from "./AppFooter";
import { Logo } from "./Logo";

export interface ShellNavItem {
  to: string;
  label: string;
  short: string;
  icon: ReactNode;
  /** Other paths whose pages belong to this entry: a detail page outside the list's own path,
      such as the pharmacist's order under /orders for the Queue entry. */
  also?: string[];
}

/** An entry is lit on its own page, on any page beneath it and on the paths it lists in `also`,
    so a detail page shows which section it belongs to (RESP-04). */
function isCurrent(pathname: string, n: ShellNavItem): boolean {
  return [n.to, ...(n.also ?? [])].some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

const COLLAPSED_KEY = "emx.sidebar.collapsed";
const SidebarCollapsed = createContext(false);

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function AppShell({
  title,
  userName,
  onSignOut,
  sidebar,
  tabBar,
  profile,
  actions,
  children,
}: {
  title: string;
  userName: string;
  onSignOut: () => void;
  sidebar: ReactNode;
  tabBar: ReactNode;
  /** Account panel contents; when set, the user name top right opens it. */
  profile?: ReactNode;
  /** Surface-specific header controls, before the account (e.g. the patient's Emergency button). */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [profileOpen, setProfileOpen] = useState(false);

  const toggleSidebar = () =>
    setCollapsed((was) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, was ? "0" : "1");
      } catch {
        /* storage blocked: still toggles for this visit */
      }
      return !was;
    });

  return (
    // Pinned to the viewport, so the page itself never scrolls — only <main> does. As h-screen
    // the shell plus the body's safe-area padding made the document taller than the screen: a
    // second scroll that jiggled the chrome. The header clears the notch, and in landscape the
    // header, content and tab bar clear the side insets (RESP-02).
    <div className="fixed inset-0 flex flex-col bg-sunken">
      {/* ── Top bar: logo hard left, surface name beside it, account right ── */}
      <header className="z-30 flex shrink-0 items-center justify-between gap-3 border-b border-line bg-surface px-safe-3 pt-safe-2.5 pb-2.5 shadow-card sm:px-safe-5">
        <div className="flex min-w-0 items-center gap-3">
          {/* Full lockup from md up; the mark alone keeps small screens tidy. `max-md:hidden`, not
              `hidden md:inline-flex`: Logo's own inline-flex beat `hidden`, so phones showed both
              logos and the title was cut short (RESP-01). */}
          <Logo to="" size={34} className="max-md:hidden" />
          <Logo to="" variant="mark" size={30} className="md:hidden" />

          <span className="hidden h-7 w-px shrink-0 bg-line sm:block" />
          <h1 className="truncate text-sm font-semibold text-brand-900 sm:text-base">{title}</h1>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {actions}
          {profile ? (
            <button
              onClick={() => setProfileOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={profileOpen}
              className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 text-sm text-ink-soft transition hover:bg-brand-50 hover:text-brand"
            >
              <Initials name={userName} />
              <span className="hidden max-w-[14rem] truncate sm:inline">{userName}</span>
            </button>
          ) : (
            <span className="hidden max-w-[14rem] truncate text-sm text-ink-soft sm:inline">{userName}</span>
          )}
          <button onClick={onSignOut} className="btn-secondary btn-sm">
            Sign out
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* ── Desktop sidebar (collapsible to an icon rail) ───────────────── */}
        <nav
          aria-label="Main"
          className={`hidden shrink-0 flex-col border-r border-line bg-surface transition-[width] duration-200 lg:flex ${
            collapsed ? "w-sidebar-collapsed" : "w-sidebar"
          }`}
        >
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            <SidebarCollapsed.Provider value={collapsed}>{sidebar}</SidebarCollapsed.Provider>
          </div>
          <button
            onClick={toggleSidebar}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`m-3 flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-ink-muted transition hover:bg-brand-50 hover:text-brand ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <ChevronIcon flipped={collapsed} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </nav>

        {/* ── Content ─────────────────────────────────────────────────────── */}
        {/* Column layout so the desktop footer sits at the bottom of short
            pages and below the content on long ones. Under `lg` the footer is
            hidden and the bottom padding clears the mobile tab bar instead. */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overscroll-contain pb-24 lg:pb-0">
          <div className="animate-fade-in flex-1 px-safe-4 py-4 sm:px-safe-6 sm:py-6">{children}</div>
          <AppFooter />
        </main>
      </div>

      {/* ── Mobile bottom tab bar ─────────────────────────────────────────── */}
      <nav className="pb-safe px-safe-0 fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface shadow-raised lg:hidden">
        {tabBar}
      </nav>

      {profile && profileOpen && <ProfilePanel onClose={() => setProfileOpen(false)}>{profile}</ProfilePanel>}
    </div>
  );
}

/** Right-hand account panel over a dimmed page; Escape, the backdrop or ✕ close it. */
function ProfilePanel({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-ink/30" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="My profile"
        className="animate-fade-in relative flex h-full w-full max-w-md flex-col bg-surface shadow-raised"
      >
        {/* Clears the notch like the app header (RESP-02). */}
        <div className="flex items-center justify-between border-b border-line px-5 pb-3 pt-safe-3">
          <h2 className="text-base font-semibold text-brand-900">My profile</h2>
          <button onClick={onClose} aria-label="Close profile" className="rounded-lg p-1.5 text-ink-muted hover:bg-brand-50 hover:text-brand">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="h-5 w-5" aria-hidden>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-5">{children}</div>
      </aside>
    </div>
  );
}

function Initials({ name }: { name: string }) {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  return (
    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand text-xs font-semibold text-brand-fg" aria-hidden>
      {letters || "?"}
    </span>
  );
}

function ChevronIcon({ flipped }: { flipped: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`h-5 w-5 shrink-0 transition-transform ${flipped ? "rotate-180" : ""}`}
      aria-hidden
    >
      <path d="m11 17-5-5 5-5M18 17l-5-5 5-5" />
    </svg>
  );
}

/** Sidebar list. Collapsed, it shows the icons only, with the label as a tooltip and for screen
 *  readers. */
export function ShellSidebar({ nav }: { nav: ShellNavItem[] }) {
  const { pathname } = useLocation();
  const collapsed = useContext(SidebarCollapsed);
  return (
    <ul className="space-y-1">
      {nav.map((n) => {
        const current = isCurrent(pathname, n);
        return (
          <li key={n.to}>
            <Link
              to={n.to}
              aria-current={current ? "page" : undefined}
              title={collapsed ? n.label : undefined}
              className={`nav-link ${collapsed ? "justify-center px-0" : ""} ${current ? "nav-link-active" : ""}`}
            >
              <span className="h-5 w-5 shrink-0">{n.icon}</span>
              <span className={collapsed ? "sr-only" : "truncate"}>{n.label}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Mobile tab bar. Scrolls horizontally when a surface has many entries (admin
 * has ten) so labels stay readable instead of being squeezed to nothing; from
 * `sm` up they share the width.
 *
 * The active tab is brought into view on every route change, and an edge fades
 * where more tabs are hidden past it (RESP-03): on /patient/profile the active
 * tab sat at x=608 on a 360px screen, out of sight.
 */
export function ShellTabBar({ nav }: { nav: ShellNavItem[] }) {
  const scrolls = nav.length > 5;
  const listRef = useRef<HTMLUListElement>(null);
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const ul = listRef.current;
    if (!ul) return;
    setHidden({ left: ul.scrollLeft > 2, right: ul.scrollLeft + ul.clientWidth < ul.scrollWidth - 2 });
  }, []);

  useEffect(() => {
    const ul = listRef.current;
    const active = ul?.querySelector<HTMLElement>('[aria-current="page"]');
    // scrollBy on the strip itself: scrollIntoView could also scroll the page.
    if (ul && active && scrolls) {
      const a = active.getBoundingClientRect();
      const u = ul.getBoundingClientRect();
      ul.scrollBy({ left: a.left - u.left - (u.width - a.width) / 2 });
    }
    measure();
  }, [pathname, scrolls, measure]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const fade = `linear-gradient(to right, ${hidden.left ? "transparent" : "#000"}, #000 1.5rem, #000 calc(100% - 1.5rem), ${hidden.right ? "transparent" : "#000"})`;

  return (
    <ul
      ref={listRef}
      onScroll={scrolls ? measure : undefined}
      className={`flex ${scrolls ? "scrollbar-none overflow-x-auto" : ""}`}
      style={scrolls ? { maskImage: fade, WebkitMaskImage: fade } : undefined}
    >
      {nav.map((n) => {
        const current = isCurrent(pathname, n);
        return (
          <li key={n.to} className={scrolls ? "w-[4.75rem] shrink-0 sm:w-auto sm:flex-1 sm:shrink" : "flex-1"}>
            <Link to={n.to} aria-current={current ? "page" : undefined} className={`nav-tab ${current ? "nav-tab-active" : ""}`}>
              <span className="h-5 w-5">{n.icon}</span>
              <span className="truncate px-1">{n.short}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
